import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FundamentalExerciseOutput } from '@jeraisonne/ai';
import { supabase } from '@/lib/supabase';

/**
 * Mutations pour les exercices fondamentaux (banque de questions Warmup).
 *
 * Un exercice fondamental est une ligne `exercises` avec `is_fundamental = true`,
 * écrite DIRECTEMENT en V2 par l'edge fn `fundamental-exercise-save` (Zéro V1) :
 * chaque question devient un bloc `statement_question` + corrigé + distracteurs.
 */

export type FundamentalExerciseInput = {
  chapter_id: string;
  lesson_id?: string | null;
  status: 'draft' | 'published' | 'archived';
  fundamental: FundamentalExerciseOutput;
};

function invalidate(qc: ReturnType<typeof useQueryClient>, lessonId?: string | null) {
  qc.invalidateQueries({ queryKey: ['tutor-exercises'] });
  qc.invalidateQueries({ queryKey: ['admin-exercises'] });
  if (lessonId) qc.invalidateQueries({ queryKey: ['lesson-exercises', lessonId] });
}

export function useCreateFundamentalExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      chapter_id,
      lesson_id,
      status,
      fundamental,
    }: FundamentalExerciseInput): Promise<string> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        exercise_id: string;
      }>('fundamental-exercise-save', {
        body: { chapter_id, lesson_id: lesson_id ?? null, status, fundamental },
      });
      if (error || !data?.exercise_id) {
        throw new Error("L'exercice n'a pas pu être enregistré. Réessaie.");
      }
      return data.exercise_id;
    },
    onSuccess: (_id, variables) => invalidate(qc, variables.lesson_id),
  });
}
